/**
 * The chosen page and every conversation about its parts, as the server keeps
 * them. One part changes at a time; the server's answer (the whole page, and the
 * part's thread) is taken as it comes, so a reload shows exactly this.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { withLive, type LivePageTime } from "../../../shared/live-page";
import { fillPageVideos, pageVideoIds, type PageVideo } from "../../../shared/page-video";
import type { PageAttachment, PageMessage, PageSection, PageTextRequest } from "../../../shared/types";
import { api, ApiError } from "../../lib/api";

/** The whole page's own thread, as the server names it. */
export const WHOLE_PAGE = "page";
/** How long a part that just changed is marked as changed. */
const LANDED_MS = 1600;

export interface PageStudio {
  /** The page as stored; null until it loads, or when the board has no page. */
  html: string | null;
  /** The page as the preview draws it: videos filled in, a live's time filled in. */
  doc: string | null;
  sections: PageSection[];
  threads: Record<string, PageMessage[]>;
  /** The part Flash is changing right now. */
  working: string | null;
  /** The part that just changed, for a moment. */
  landed: string | null;
  /** The failed-change line of one part's card: it belongs to that part and that attempt only. */
  errorIn: (section: string) => string;
  /** A card opened or closed: whatever failed before is not its to show. */
  clearError: () => void;
  ask: (section: string, text: string, files: PageAttachment[]) => Promise<boolean>;
  back: (section: string, messageId: string) => Promise<void>;
  /** The host's own words in one slot of a part (its `index` among the slots of that name). */
  words: (section: string, slot: string, index: number, text: string, line?: PageTextRequest["line"]) => Promise<void>;
  /** The part as it was before its latest change, for holding Before. */
  before: (section: string) => string | null;
  /** A page as the preview draws it (the whole page held as it was, for Before). */
  docOf: (html: string) => string;
  /** The last change Flash made to the page: its part and the reply that carries the way back. */
  lastChange: { section: string; id: string } | null;
}

/** How long after the chapters change the page is read again: the board save refills it first. */
const REFILL_MS = 1800;

export function usePageStudio(webinarId: string, live: LivePageTime | null, chapterKey: string): PageStudio {
  const [html, setHtml] = useState<string | null>(null);
  const [sections, setSections] = useState<PageSection[]>([]);
  const [threads, setThreads] = useState<Record<string, PageMessage[]>>({});
  const [working, setWorking] = useState<string | null>(null);
  const [landed, setLanded] = useState<string | null>(null);
  const [failure, setFailure] = useState<{ section: string; text: string } | null>(null);
  const errorIn = useCallback((section: string) => (failure?.section === section ? failure.text : ""), [failure]);
  const clearError = useCallback(() => setFailure(null), []);
  const [lastChange, setLastChange] = useState<{ section: string; id: string } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const gone = useRef(false);

  useEffect(() => {
    gone.current = false;
    void api.page
      .get(webinarId)
      .then((got) => {
        if (gone.current) return;
        setHtml(got.html);
        setSections(got.sections);
        setThreads(got.threads);
      })
      .catch(() => undefined);
    return () => {
      gone.current = true;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [webinarId]);

  // A chapter renamed on the page goes to the board's chapters card; the save refills the page from it.
  const chapterKeyWas = useRef(chapterKey);
  useEffect(() => {
    if (chapterKeyWas.current === chapterKey) return;
    chapterKeyWas.current = chapterKey;
    const t = window.setTimeout(() => {
      void api.page
        .get(webinarId)
        .then((got) => {
          if (gone.current) return;
          setHtml(got.html);
          setSections(got.sections);
        })
        .catch(() => undefined);
    }, REFILL_MS);
    return () => window.clearTimeout(t);
  }, [chapterKey, webinarId]);

  // The page's videos are filled in here, never in the stored page, so nothing with a token is saved.
  const videoIds = html ? pageVideoIds(html).join(",") : "";
  const [videos, setVideos] = useState<{ ids: string; map: Record<string, PageVideo>; lib: string } | null>(null);
  useEffect(() => {
    if (!videoIds) return;
    let alive = true;
    api.page
      .videos(webinarId)
      .then((r) => alive && setVideos({ ids: videoIds, map: r.videos, lib: r.lib }))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [webinarId, videoIds]);

  const docOf = useCallback(
    (page: string) => {
      const timed = live ? withLive(page, live, Date.now()) : page;
      return videos ? fillPageVideos(timed, videos.map, videos.lib) : timed;
    },
    [live, videos],
  );
  const doc = useMemo(() => {
    if (html === null) return null;
    const timed = live ? withLive(html, live, Date.now()) : html;
    return videos && videoIds && videos.ids === videoIds ? fillPageVideos(timed, videos.map, videos.lib) : timed;
  }, [html, live, videos, videoIds]);

  const mark = useCallback((section: string) => {
    setLanded(section);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setLanded(null), LANDED_MS);
  }, []);

  const ask = useCallback(
    async (section: string, text: string, files: PageAttachment[]) => {
      if (working) return false;
      setWorking(section);
      setFailure(null);
      // The host's words show at once, the way a chat does, while Flash works.
      const mine: PageMessage = { id: `pending-${Date.now()}`, who: "me", text: text || "See the attached files.", at: Math.round(Date.now() / 1000), ...(files.length ? { files } : {}) };
      setThreads((t) => ({ ...t, [section]: [...(t[section] ?? []), mine] }));
      try {
        const res = await api.page.ask(webinarId, { section, text, ...(files.length ? { files } : {}) });
        if (gone.current) return true;
        setHtml(res.html);
        setSections(res.sections);
        setThreads((t) => ({ ...t, [section]: res.thread }));
        if (res.applied) {
          mark(section);
          const reply = res.thread.at(-1);
          if (reply) setLastChange({ section, id: reply.id });
        }
        return true;
      } catch (e) {
        if (gone.current) return false;
        setThreads((t) => ({ ...t, [section]: (t[section] ?? []).filter((m) => m.id !== mine.id) }));
        setFailure({ section, text: e instanceof ApiError ? e.message : "That change did not go through. Try again." });
        return false;
      } finally {
        if (!gone.current) setWorking(null);
      }
    },
    [mark, webinarId, working],
  );

  const back = useCallback(
    async (section: string, messageId: string) => {
      setFailure(null);
      try {
        const res = await api.page.back(webinarId, { section, messageId });
        if (gone.current) return;
        setHtml(res.html);
        setSections(res.sections);
        setThreads((t) => ({ ...t, [section]: res.thread }));
        mark(section);
      } catch (e) {
        if (!gone.current) setFailure({ section, text: e instanceof ApiError ? e.message : "That could not be taken back." });
      }
    },
    [mark, webinarId],
  );

  const words = useCallback(
    async (section: string, slot: string, index: number, text: string, line?: PageTextRequest["line"]) => {
      setFailure(null);
      try {
        const res = await api.page.text(webinarId, { section, slot, index, text, ...(line ? { line } : {}) });
        if (gone.current) return;
        setHtml(res.html);
        setSections(res.sections);
        mark(section);
      } catch (e) {
        if (!gone.current) setFailure({ section, text: e instanceof ApiError ? e.message : "Those words could not be saved." });
      }
    },
    [mark, webinarId],
  );

  const before = useCallback(
    (section: string) => {
      const last = (threads[section] ?? []).findLast((m) => m.who === "ai" && m.before !== undefined && !m.taken);
      return last?.before ?? null;
    },
    [threads],
  );

  return { html, doc, sections, threads, working, landed, errorIn, clearError, ask, back, words, before, docOf, lastChange };
}

/** How many times the host asked about a part (the badge on it). */
export function asksIn(thread: readonly PageMessage[] | undefined): number {
  return (thread ?? []).filter((m) => m.who === "me" && !m.taken).length;
}
